import { useEffect, useRef, useState } from 'react'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

export const useRecaptcha = () => {
  const recaptchaRef = useRef(null)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const isEnabled = Boolean(RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim())

  useEffect(() => {
    if (!isEnabled) return undefined

    window.handleRecaptchaCallback = (token) => {
      setRecaptchaToken(token)
    }

    window.handleRecaptchaExpired = () => {
      setRecaptchaToken(null)
    }

    window.handleRecaptchaError = () => {
      setRecaptchaToken(null)
    }

    const renderRecaptchaWidget = () => {
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.ready(() => {
          try {
            if (!recaptchaRef.current.hasChildNodes()) {
              window.grecaptcha.render(recaptchaRef.current, {
                sitekey: RECAPTCHA_SITE_KEY,
                callback: window.handleRecaptchaCallback,
                'expired-callback': window.handleRecaptchaExpired,
                'error-callback': window.handleRecaptchaError
              })
            }
            setRecaptchaLoaded(true)
          } catch {
            setRecaptchaLoaded(true)
          }
        })
      }
    }

    if (!window.grecaptcha && !document.querySelector('script[src*="recaptcha"]')) {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js'
      script.async = true
      script.defer = true
      script.onload = () => {
        setRecaptchaLoaded(true)
        renderRecaptchaWidget()
      }
      script.onerror = () => setRecaptchaLoaded(false)
      document.head.appendChild(script)
    } else if (window.grecaptcha) {
      setRecaptchaLoaded(true)
      renderRecaptchaWidget()
    } else {
      const checkInterval = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(checkInterval)
          setRecaptchaLoaded(true)
          renderRecaptchaWidget()
        }
      }, 100)

      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval)
        if (!window.grecaptcha) setRecaptchaLoaded(false)
      }, 10000)

      return () => {
        clearInterval(checkInterval)
        clearTimeout(timeoutId)
      }
    }

    return () => {
      delete window.handleRecaptchaCallback
      delete window.handleRecaptchaExpired
      delete window.handleRecaptchaError
    }
  }, [isEnabled])

  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaRef.current) {
      try {
        window.grecaptcha.reset()
      } catch {
        // ignore reset errors
      }
    }
    setRecaptchaToken(null)
  }

  return {
    recaptchaRef,
    recaptchaToken,
    recaptchaLoaded,
    isEnabled,
    resetRecaptcha
  }
}
